import { useSyncExternalStore } from "react";

// Server-enforced Ask-AI quota client. The /chat, /quota/status and /quota/verify calls go to the
// API directly (VITE_CONTENT_API_URL) with an x-visitor-id header; the CAPTCHA-gated email request
// goes same-origin to /api/quota/email through CloudFront so the AWS WAF JS SDK can read the WAF
// challenge/block response (which carries no CORS headers cross-origin).

const API = (import.meta.env.VITE_CONTENT_API_URL ?? "").replace(/\/$/, "");
export const quotaEnabled = Boolean(API);
// The email top-up tier needs the WAF CAPTCHA API key baked in (a one-time bootstrap, like the API
// URL). Until it's set, the "share email for more" action stays hidden and the rest works.
export const WAF_API_KEY = (import.meta.env.VITE_WAF_CAPTCHA_API_KEY ?? "").trim();
export const WAF_SDK_URL = (import.meta.env.VITE_WAF_SDK_URL ?? "").trim();
export const emailTierEnabled = Boolean(API) && Boolean(WAF_API_KEY) && Boolean(WAF_SDK_URL);

interface WafIntegration {
  fetch: (input: string, init?: RequestInit) => Promise<Response>;
}
interface WafCaptcha {
  renderCaptcha: (
    el: HTMLElement,
    opts: { apiKey: string; onSuccess: (token: string) => void; onError?: (e?: unknown) => void },
  ) => void;
}
declare global {
  interface Window {
    AwsWafIntegration?: WafIntegration;
    AwsWafCaptcha?: WafCaptcha;
  }
}

const VID_KEY = "ir.visitor.v1";
export function getVisitorId(): string {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) {
      v = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `v-${Date.now().toString(36)}`;
      localStorage.setItem(VID_KEY, v);
    }
    return v;
  } catch {
    return "";
  }
}

export type QuotaStatus = "ok" | "quota" | "ip" | "paused" | "error";
interface QuotaState {
  remaining: number | null; // null until the server is asked
  status: QuotaStatus;
}
let state: QuotaState = { remaining: null, status: "ok" };
const listeners = new Set<() => void>();
function set(next: Partial<QuotaState>): void {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

/** Reactive quota state for the chat UI. */
export function useQuota(): QuotaState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
    () => state,
  );
}
/** Out of questions (but not a server pause — the pause renders its own notice). */
export const isLocked = (s: QuotaState): boolean => s.status !== "paused" && s.remaining !== null && s.remaining <= 0;

/** Read the server's view of remaining questions (called on mount). */
export async function refreshQuota(): Promise<void> {
  if (!API) return;
  try {
    const res = await fetch(`${API}/quota/status`, { headers: { "x-visitor-id": getVisitorId() } });
    if (!res.ok) return;
    const d = (await res.json()) as { remaining?: number };
    if (typeof d.remaining === "number") set({ remaining: d.remaining, status: d.remaining <= 0 ? "quota" : "ok" });
  } catch {
    // keep current state — best-effort
  }
}

export interface AskResult {
  ok: boolean;
  answer?: string;
  status: QuotaStatus;
}
/** Ask a question. Updates the shared quota state as a side effect, returns the answer (or status). */
export async function askChat(question: string, history: { role: string; content: string }[]): Promise<AskResult> {
  if (!API) {
    return { ok: true, answer: "The AI assistant answers from my résumé once the backend is deployed.", status: "ok" };
  }
  try {
    const res = await fetch(`${API}/chat`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-visitor-id": getVisitorId() },
      body: JSON.stringify({ question, history }),
    });
    const data = (await res.json().catch(() => ({}))) as { answer?: string; remaining?: number; reason?: string };
    if (res.status === 503) {
      set({ status: "paused" });
      return { ok: false, status: "paused" };
    }
    if (res.status === 429) {
      const st: QuotaStatus = data.reason === "ip" ? "ip" : "quota";
      set({ remaining: 0, status: st });
      return { ok: false, status: st };
    }
    if (!res.ok || !data.answer) return { ok: false, status: "error" };
    if (typeof data.remaining === "number") set({ remaining: data.remaining, status: data.remaining <= 0 ? "quota" : "ok" });
    return { ok: true, answer: data.answer, status: "ok" };
  } catch {
    return { ok: false, status: "error" };
  }
}

/** Redeem a magic-link token for more questions. Returns the new remaining + granted count. */
export async function verifyQuotaLink(token: string): Promise<{ ok: boolean; remaining?: number; granted?: number }> {
  if (!API) return { ok: false };
  try {
    const res = await fetch(`${API}/quota/verify`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-visitor-id": getVisitorId() },
      body: JSON.stringify({ token }),
    });
    const d = (await res.json().catch(() => ({}))) as { ok?: boolean; remaining?: number; granted?: number };
    if (d.ok && typeof d.remaining === "number") set({ remaining: d.remaining, status: d.remaining > 0 ? "ok" : "quota" });
    return { ok: Boolean(d.ok), remaining: d.remaining, granted: d.granted };
  } catch {
    return { ok: false };
  }
}

// A ?qlink redeemed during boot can resolve before <AskDock> has mounted its event listener, which
// would drop the quota-granted event (and with it the "added N questions" banner + auto-open). Buffer
// the granted count here so the dock can pick it up on mount even if it missed the live event.
let pendingGrant = 0;
/** Read and clear a grant that landed before the dock was listening. Returns 0 if none. */
export function takePendingGrant(): number {
  const n = pendingGrant;
  pendingGrant = 0;
  return n;
}

/** On load: redeem a ?qlink token, then strip it from the URL (mirrors the register ?mlink flow). */
export async function consumeQlink(): Promise<{ granted?: number } | null> {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("qlink");
  if (!token || !API) return null;
  let result: { granted?: number } | null = null;
  try {
    const r = await verifyQuotaLink(token);
    if (r.ok) {
      result = { granted: r.granted };
      if (r.granted && r.granted > 0) pendingGrant = r.granted;
    }
  } finally {
    params.delete("qlink");
    const q = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : "") + window.location.hash);
  }
  return result;
}

/** WAF-gated email request — must go through AwsWafIntegration.fetch so the token is attached. */
export async function submitEmailGrant(email: string): Promise<{ ok: boolean; error?: string; captcha?: boolean }> {
  const waf = window.AwsWafIntegration;
  if (!emailTierEnabled || !waf?.fetch) return { ok: false, error: "The email option isn't available right now." };
  try {
    const res = await waf.fetch("/api/quota/email", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, visitorUuid: getVisitorId() }),
    });
    if (res.status === 405) return { ok: false, captcha: true }; // token expired/needed → re-challenge
    const d = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
    return { ok: Boolean(d.ok), error: d.ok ? undefined : (d.error ?? "Couldn't send the link.") };
  } catch {
    return { ok: false, error: "Network error — please retry." };
  }
}
